import React from "react";

import { Download } from "lucide-react";
import { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";

import { Button } from "@components/ui/button";

import { cn } from "@utils/ui";

type FileDropzoneProps = {
  /** Function to get dropzone root props from react-dropzone */
  getRootProps: () => DropzoneRootProps;
  /** Function to get dropzone input props from react-dropzone */
  getInputProps: () => DropzoneInputProps;
  /** Reference to the hidden file input element */
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  /** Whether files are currently being dragged over the dropzone */
  isDragActive: boolean;
  /** Whether the dropzone is disabled */
  disabled: boolean;
  /** Placeholder text to display in the dropzone */
  placeholder: string;
  /** Array of allowed file types (e.g., ['.pdf', '.jpg', 'image/*']) */
  allowedTypes?: string[];
  /** Maximum file size in bytes */
  maxSize: number;
  /** Size variant of the dropzone */
  size: "sm" | "default";
  /** Whether to show the file selection button */
  showSelectButton: boolean;
  /** Additional CSS class names for the dropzone */
  dropzoneClassName?: string;
  /** Function to format file size for display */
  formatFileSize: (bytes: number) => string;
};

/**
 * FileDropzone component
 *
 * Displays a drag-and-drop area for file selection.
 * Shows file type restrictions and maximum file size information.
 *
 * @param getRootProps - Function to get dropzone root props from react-dropzone
 * @param getInputProps - Function to get dropzone input props from react-dropzone
 * @param fileInputRef - Reference to the hidden file input element
 * @param isDragActive - Whether files are currently being dragged over the dropzone
 * @param disabled - Whether the dropzone is disabled
 * @param placeholder - Placeholder text to display in the dropzone
 * @param allowedTypes - Array of allowed file types (e.g., ['.pdf', '.jpg', 'image/*'])
 * @param maxSize - Maximum file size in bytes
 * @param size - Size variant of the dropzone ("sm" or "default")
 * @param showSelectButton - Whether to show the file selection button
 * @param dropzoneClassName - Additional CSS class names for the dropzone
 * @param formatFileSize - Function to format file size for display
 */
export const FileDropzone = ({
  getRootProps,
  getInputProps,
  fileInputRef,
  isDragActive,
  disabled,
  placeholder,
  allowedTypes,
  maxSize,
  size,
  showSelectButton,
  dropzoneClassName,
  formatFileSize,
}: FileDropzoneProps) => {
  return (
    <div
      {...getRootProps()}
      className={cn(
        "cursor-pointer rounded-lg border-2 border-dashed transition-colors",
        "hover:border-primary-400 hover:bg-primary-50",
        size === "sm" ? "flex items-center gap-3 p-3" : "p-6 text-center",
        isDragActive ? "border-primary bg-primary-50" : "border-border",
        disabled && "cursor-not-allowed opacity-50",
        dropzoneClassName
      )}
    >
      <input {...getInputProps()} ref={fileInputRef} />

      <figure
        className={cn(
          "rounded-full bg-success-50",
          size === "sm" ? "shrink-0 p-2" : "mx-auto inline-block p-4"
        )}
      >
        <Download
          className={cn(
            "font-bold text-success-600",
            size === "sm" ? "size-4" : "size-5"
          )}
        />
      </figure>

      <div className={cn("flex-1", size === "sm" ? "text-left" : "")}>
        <p
          className={cn(
            "font-medium text-foreground",
            size === "sm" ? "text-sm" : "mb-2 text-lg"
          )}
        >
          {isDragActive ? "Drop files here" : placeholder}
        </p>
        <p
          className={cn(
            "text-foreground-muted",
            size === "sm" ? "text-xs" : "text-sm"
          )}
        >
          {allowedTypes && `Supported file formats: ${allowedTypes.join(", ")}`}
          {maxSize && ` (max: ${formatFileSize(maxSize)})`}
        </p>
      </div>

      {showSelectButton && (
        <Button
          type="button"
          className={cn(size === "sm" ? "" : "mt-4")}
          size={size === "sm" ? "sm" : "default"}
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          disabled={disabled}
        >
          Select File
        </Button>
      )}
    </div>
  );
};
